import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import MergeFieldsSection from './MergeFieldsSection';
import MergeStudentsSection from './MergeStudentsSection';
import toast from '../../i18n/toast';
import { useTranslation } from 'react-i18next';

const ClientMergeDialog = ({
  show,
  sourceClient,
  targetClient,
  studentsA,
  studentsB,
  onConfirm,
  onClose
}) => {
  const { t: tr } = useTranslation();
  const [selectedFields, setSelectedFields] = useState({});
  const [studentMerges, setStudentMerges] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize with defaults (prefer source/edited client, enable merging by default)
  useEffect(() => {
    if (show) {
      setSelectedFields({});
      // Initialize student merges with merge=true by default
      const defaultMerges = {};
      studentsB.forEach(studentB => {
        // Check if there's a matching student in A (by name)
        const matchingStudentA = studentsA.find(studentA =>
          studentA.firstName === studentB.firstName
        );
        if (matchingStudentA) {
          defaultMerges[studentB.id] = {
            merge: true,
            mergeWith: matchingStudentA.id
          };
        }
      });
      setStudentMerges(defaultMerges);
    }
  }, [show, studentsA, studentsB]);

  const handleFieldSelect = (fieldKey, selection) => {
    setSelectedFields(prev => ({
      ...prev,
      [fieldKey]: selection
    }));
  };

  const handleSuggestionChange = (studentBId, decision) => {
    setStudentMerges(prev => ({
      ...prev,
      [studentBId]: decision
    }));
  };

  const handleConfirm = () => {
    setIsSubmitting(true);

    const mergeData = {
      sourceId: sourceClient.id,
      targetId: targetClient.id,
      fieldSelections: selectedFields,
      studentMerges
    };

    onConfirm(mergeData)
      .then(() => {
        setIsSubmitting(false);
        onClose();
      })
      .catch((error) => {
        setIsSubmitting(false);
        onClose();
        toast.error(error.message);
      });
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton closeLabel={tr("common:close")}>
        <Modal.Title>{tr("manage:mergeClients_137")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-6">
            <h6 className="text-primary">{tr("manage:clientAPrimary_138")}</h6>
            <p className="mb-1">
              <strong>{sourceClient.firstName} {sourceClient.lastName}</strong>
            </p>
            <small className="text-muted">{tr("manage:id_139")} {sourceClient.id}</small>
          </div>
          <div className="col-md-6">
            <h6 className="text-secondary">{tr("manage:clientBToBeRemoved_140")}</h6>
            <p className="mb-1">
              <strong>{targetClient.firstName} {targetClient.lastName}</strong>
            </p>
            <small className="text-muted">{tr("manage:id_139")} {targetClient.id}</small>
          </div>
        </div>

        <hr />

        <MergeFieldsSection
          clientA={sourceClient}
          clientB={targetClient}
          selectedFields={selectedFields}
          onFieldSelect={handleFieldSelect}
        />

        <hr />

        <MergeStudentsSection
          studentsA={studentsA}
          studentsB={studentsB}
          suggestions={studentMerges}
          onSuggestionChange={handleSuggestionChange}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={isSubmitting}>{tr("common:cancel")}</Button>
        <Button
          variant="success"
          onClick={handleConfirm}
          disabled={isSubmitting}
          className="fw-bold"
        >
          {isSubmitting ? 'Scalanie...' : tr("manage:mergeClients_141")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ClientMergeDialog.propTypes = {
  show: PropTypes.bool.isRequired,
  sourceClient: PropTypes.object.isRequired,
  targetClient: PropTypes.object.isRequired,
  studentsA: PropTypes.array.isRequired,
  studentsB: PropTypes.array.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ClientMergeDialog;
